import { useState, useEffect } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useDebouncedValue } from './useDebouncedValue';
import useWithGA from './useWithGA';

import useTransformGatsbyData from './useTransformGatsbyData';

const SEARCH_QUERY = graphql`
  query {
    bwSearchData {
      data
    }
  }
`;

const useSearch = (big = false) => {
  const data = useStaticQuery(SEARCH_QUERY);
  const bwSearchData = useTransformGatsbyData(data.bwSearchData);

  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const debouncedQuery = useDebouncedValue(searchValue, 200);
  const { handleDataLayer } = useWithGA();

  useEffect(() => {
    let tempFilteredSuggestions = [];
    let tempShowSuggestions = false;

    const searchQuery = debouncedQuery
      .toLowerCase()
      .replace(/[\W_]+/gi, '')
      .trim();
    if (searchQuery.length >= 1) {
      tempFilteredSuggestions = bwSearchData.filter((suggestion) => {
        const isSubCatOrCat = ['category', 'subCategory'].includes(
          suggestion.type
        );

        if (isSubCatOrCat) {
          const matchesName = suggestion.name
            .toLocaleLowerCase()
            .replace(/[\W_]+/gi, '')
            .includes(searchQuery);

          const matchesAlternativeName = suggestion.alternativeNames
            ?.map((element) => element.alternativeName)
            .filter((alternativeName) => !!alternativeName)
            .find((alternativeName) =>
              alternativeName
                .toLocaleLowerCase()
                .replace(/[\W_]+/gi, '')
                .includes(searchQuery)
            );

          return matchesName || matchesAlternativeName;
        }

        if (suggestion.type === 'system') {
          const matchesSystemName = suggestion.name
            ?.toLocaleLowerCase()
            .replace(/[\W_]+/gi, '')
            .includes(searchQuery);

          const matchesCompanyName = suggestion.companyName
            ?.toLocaleLowerCase()
            .replace(/[\W_]+/gi, '')
            .includes(searchQuery);

          return matchesSystemName || matchesCompanyName;
        }

        return false;
      });

      tempShowSuggestions = true;
    }

    setSearchSuggestions(tempFilteredSuggestions);
    setSearchVisible(tempShowSuggestions);

    if (
      searchValue.length > 0 &&
      tempShowSuggestions &&
      tempFilteredSuggestions.length === 0
    ) {
      onSearchMiss();
    }
  }, [debouncedQuery, bwSearchData]);

  const onSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();

    handleDataLayer({
      event_name: 'search',
      event_action: 'Submit search',
      search_term: searchValue,
    });
  };

  const onSearchClick = (name) => {
    document.querySelector('html').classList.remove('scroll-lock');

    handleDataLayer({
      event_name: 'search',
      event_action: `Search result click ${big ? '- large' : '- nav'}`,
      search_term: searchValue,
      search_result_click: name,
    });
  };

  const onSearchMiss = () => {
    handleDataLayer({
      event_name: 'search',
      event_action: `Search result miss`,
      search_term: searchValue,
    });
  };

  return {
    searchSuggestions,
    searchVisible,
    searchValue,
    onSearchChange,
    onSearchSubmit,
    onSearchClick,
  };
};

export default useSearch;
